// Tipos de domínio do sandbox de simulação, 1:1 com useSandboxDraft.
// Os contratos de payload (ScenarioStructure, ScenarioMachineEntry, ScenarioEdge, SimProfile,
// SimOverride, SimulationPeriod, OrgMachineItem) vivem em @opus-dt/shared-types.
import type {
  CalibrationDays,
  PairSimProfile,
  SandboxStage,
  ScenarioCalendarOverride,
  ScenarioEdge,
  ScenarioStructure,
  SimOverride,
  SimProfile,
  SimulationObjective,
  SimulationPeriod,
} from "@opus-dt/shared-types";
import { MEASURABLE_PARAMS, originOf } from "@opus-dt/shared-types";

export type {
  CalibrationDays,
  RunLevel,
  SandboxStage,
  ScenarioEdge,
  ScenarioStructure,
} from "@opus-dt/shared-types";

// Derivação pura de edges a partir dos estágios — usada no save (toScenarioStructure).
import { deriveEdgesFromStages } from "@/features/simulations/sandbox/SandboxCanvas/flow-helpers";

/** Resposta de GET /scenarios/:id — entidade de detalhe do cenário consumida pelo sandbox.*/
export interface ScenarioDetail {
  id: string;
  name: string;
  lineId: string | null;
  productId?: string | null;
  structureSnap: ScenarioStructure | null;
  updatedAt: string;
  lineDrift: boolean;
  // Escolha de calendário do cenário (herdar/24-7/cópia); null ⇒ default (inherit com
  // linha base; always_on em linha nova).
  calendarOverride: ScenarioCalendarOverride | null;
  // Última execução deste cenário. Reabrir um cenário salvo deve devolver a
  // PERGUNTA e a RESPOSTA; sem isso o usuário reencontrava a tela em branco e pagava um lote
  // inteiro para rever o que já fora calculado.
  lastRun: {
    id: string;
    objective: SimulationObjective;
    targetQuantity: number | null;
    productionOrderId: string | null;
    horizonHours: number;
  } | null;
}

/** Estado visual de um nó de máquina no canvas por estágios (cor + rótulo/ícone,
 * nunca só cor). "sem comunicação" = Machine.status === "NODATA".*/
export type SandboxNodeState =
  | "normal"
  | "bottleneck"
  | "off"
  | "incompatible"
  | "added"
  | "nodata";

/** Dados de um nó de máquina do React Flow no sandbox. UI-only.*/
export interface SandboxFlowNodeData {
  label: string;
  code: string;
  present: boolean;
  origin: "line" | "acervo";
  stageKey: string;
  state: SandboxNodeState;
  provenance?: "measured" | "assumed";
}

/** Dados de um nó de fundo STAGE_LANE (header de estágio embutido no canvas). `isGhost` é a lane
 * final "+ Estágio" (sem stageKey real). UI-only.*/
export interface SandboxLaneNodeData {
  stageKey: string;
  name: string;
  count: number;
  /** Posição na ordem de estágios (0-based); `isFirst`/`isLast` se derivam disto + `totalStages`.*/
  index: number;
  totalStages: number;
  editable: boolean;
  isGhost: boolean;
  onAddMachine?: (stageKey: string) => void;
  /** Vazão estimada do estágio (un/h); null enquanto não há estimativa.*/
  throughputUnH?: number | null;
  /** Estágio que limita a linha. Sempre acompanhado de rótulo — nunca só cor.*/
  isBottleneck?: boolean;
}

/** Máquina no rascunho do sandbox: campos do snapshot + display (code/name/posição).*/
export interface SandboxMachine {
  key: string;
  sourceMachineId: string;
  origin: "line" | "acervo";
  present: boolean;
  code: string;
  name: string;
  x: number;
  y: number;
  /** Estágio (camada) a que a máquina pertence — referencia SandboxStage.key.*/
  stageKey: string;
  simProfile?: SimProfile;
  simOverrides?: SimOverride[];
  /** Base MEDIDA por máquina resolvida pelo preview do servidor na janela de calibração.
   * É base de EXIBIÇÃO — o sheet usa como base do slider quando não há simProfile autorado; NÃO é
   * persistida no snapshot (fica fora de `toScenarioStructure`) porque depende da janela e se
   * re-deriva a cada preview. Distingue-se de `simProfile` (autoria/override do usuário).*/
  measuredBase?: SimProfile;
  /** Perfil autorado do par máquina×produto ATIVO, entregue pelo preview do servidor
   * (decisão Q3: servidor resolve o dado, cliente aplica com `applyPairProfile`). Display-only
   * como `measuredBase`: não é persistido no snapshot e se re-resolve a cada preview. Vence o
   * `simProfile` autorado por máquina — o par é mais específico —, nunca um param medido.*/
  pairProfile?: PairSimProfile | null;
  /** Contador de produção da máquina real (display-only como measuredBase):
   * decide o aviso estrutural×transitório no sheet. Ausente até o preview do servidor chegar.*/
  productionCounter?: { configured: boolean; producedInWindow: number };
  /** Origem do perfil de simulação p/ o badge de proveniência.*/
  provenance?: "measured" | "assumed";
}

/** Período do sandbox. FREE = intervalo livre; OP_* = janela/produto de uma Ordem de Produção.
 * Mapeia p/ SimulationPeriod no submit via `toSimulationPeriod`.*/
export interface SandboxPeriod {
  mode: "FREE" | "OP_PLANNED" | "OP_EXECUTED";
  periodStart: string | null;
  periodEnd: string | null;
  /** Janela de calibração discreta. Vazio ⇒ default 90 do schema no submit
   * (guardrail de campos numéricos: nunca forçar literal).*/
  calibrationDays: CalibrationDays | undefined;
  /** Modos OP: OP de origem; horizonte/produto vêm dela no backend.*/
  sourceProductionOrderId: string | null;
}

/** Nó âncora (START/END) do canvas do cenário.*/
export interface SandboxAnchor {
  key: string;
  x: number;
  y: number;
}

export interface SandboxDraft {
  scenarioId: string | null;
  baseLineId: string | null;
  baseUpdatedAt: string | null;
  productId: string | null;
  machines: Record<string, SandboxMachine>;
  /** Estágios (camadas) do pipeline; edges são derivadas destes no save.*/
  stages: SandboxStage[];
  edges: ScenarioEdge[];
  start: SandboxAnchor;
  end: SandboxAnchor;
  period: SandboxPeriod;
  selectedKey: string | null;
  dirty: boolean;
}

/** Resultado enxuto de uma rodada, para baseline e comparação.*/
export interface RunKpis {
  oee: number | null;
  availability: number | null;
  performance: number | null;
  quality: number | null;
  estimatedProduction: number | null;
  confidence: string | null;
  bottleneckNames: string[];
}

/** Comparação candidato × baseline (deltas em pontos/percentual).*/
export interface ComparisonResult {
  deltaOee: number | null;
  deltaProduction: number | null;
  bottleneckShifted: boolean;
}

export const DEFAULT_PERIOD: SandboxPeriod = {
  mode: "FREE",
  periodStart: null,
  periodEnd: null,
  calibrationDays: undefined,
  sourceProductionOrderId: null,
};

/** Máquina "medida" p/ o badge do nó (resumo grosso): sinal de bootstrap do acervo
 * (`provenance`) OU qualquer parâmetro derivável medido (origem por parâmetro). Mantém o badge do
 * nó consistente com os badges por-parâmetro do sheet (que também usam `originOf`).*/
export function machineMeasured(
  m: Pick<SandboxMachine, "provenance" | "simProfile" | "measuredBase">
): boolean {
  if (m.provenance === "measured") return true;
  const src = m.simProfile ?? m.measuredBase;
  return MEASURABLE_PARAMS.some((p) => originOf(src, p).provenance === "measured");
}

/** True quando o período tem o necessário p/ rodar: FREE exige fim > início; OP exige uma OP.*/
export function periodReady(period: SandboxPeriod): boolean {
  if (period.mode === "FREE") {
    return (
      !!period.periodStart &&
      !!period.periodEnd &&
      new Date(period.periodEnd) > new Date(period.periodStart)
    );
  }
  return !!period.sourceProductionOrderId;
}

/**
 * Simular exige período definido?
 *
 * NÃO no modo livre. A ação única enfileira um LOTE, e o lote tira janela e prazo do
 * OBJETIVO (`toBatchInput` nem lê o período). Exigir um intervalo era herança da rodada avulsa, e
 * como todo cenário criado pelo hub nasce sem período, o botão Simular nascia morto justamente no
 * caminho novo. No modo OP a ordem continua obrigatória: é dela que saem produto e janela.
 */
export function periodReadyToSimulate(period: SandboxPeriod): boolean {
  return period.mode === "FREE" || periodReady(period);
}

/** Mapeia o período do sandbox para o contrato SimulationPeriod (submit da execução).
 * Campo de calibração vazio ⇒ default 90 do schema (aplicado aqui p/ satisfazer o output type).*/
export function toSimulationPeriod(period: SandboxPeriod): SimulationPeriod {
  if (period.mode === "FREE") {
    return {
      mode: "FREE",
      periodStart: period.periodStart ?? undefined,
      periodEnd: period.periodEnd ?? undefined,
      calibrationDays: period.calibrationDays ?? 90,
    };
  }
  return {
    mode: period.mode,
    sourceProductionOrderId: period.sourceProductionOrderId ?? undefined,
    calibrationDays: period.calibrationDays ?? 90,
  };
}

export function toScenarioStructure(draft: SandboxDraft): ScenarioStructure {
  const machines = Object.values(draft.machines);
  return {
    machines: machines.map((m) => ({
      key: m.key,
      sourceMachineId: m.sourceMachineId,
      origin: m.origin,
      present: m.present,
      stageKey: m.stageKey,
      simProfile: m.simProfile,
      simOverrides: m.simOverrides,
    })),
    // Estágios são a fonte de autoria; as edges PRODUCTION_FLOW são
    // DERIVADAS deles no save (self-contained, independe do store ter recomputado).
    stages: draft.stages,
    edges: deriveEdgesFromStages(machines, draft.stages),
    canvas: {
      nodes: [
        ...machines.map((m) => ({ key: m.key, x: m.x, y: m.y, nodeType: "MACHINE" as const })),
        { key: draft.start.key, x: draft.start.x, y: draft.start.y, nodeType: "START" as const },
        { key: draft.end.key, x: draft.end.x, y: draft.end.y, nodeType: "END" as const },
      ],
    },
    productId: draft.productId ?? "",
    baseLineId: draft.baseLineId,
  };
}

// ── Objetivo da simulação ───────────────────────────────────────────────────
// Mora em `sim-objective.types.ts` (guardrail #1); re-exportado aqui para que os consumidores não
// precisem saber da divisão.
export type { ObjectiveWindow, SimulationObjectiveChoice } from "./sim-objective.types";
export { objectiveMissingLabel, objectiveReady } from "./sim-objective.types";
